import axios from 'axios';
import { getIdToken, clearSession } from './utils/authStorage';
import {
  TENANT_SESSION_HEADER,
  clearTenantSession,
  getTenantSession,
  isTenantPortalPage,
  isTenantSessionUrl
} from './utils/tenantSession';
import { PHOTO_UPLOAD_TIMEOUT_MS } from './utils/photoUpload';

// Create axios instance with proper base URL
// Determine if we're in production based on hostname
const getBaseURL = () => {
  console.log('🔍 getBaseURL called');
  console.log('🔍 typeof window:', typeof window);
  
  // Check if we're in production first
  let isProduction = false;
  let isStagingApp = false;
  let isLocalNetwork = false;
  let frontendHostname = 'localhost';
  
  if (typeof window !== 'undefined' && window.location) {
    frontendHostname = window.location.hostname;
    isStagingApp =
      frontendHostname === 'staging.app.zenrentflow.com' ||
      frontendHostname === 'staging-app-zenrentflow-com.web.app' ||
      frontendHostname === 'staging-app-zenrentflow-com.firebaseapp.com';
    isProduction = !isStagingApp && (
                   frontendHostname === 'app.zenrentflow.com' || 
                   frontendHostname === 'app-zenrentflow-com.web.app' ||
                   frontendHostname === 'app-zenrentflow-com.firebaseapp.com' ||
                   frontendHostname.includes('zenrentflow') ||
                   (frontendHostname !== 'localhost' && frontendHostname !== '127.0.0.1' && !frontendHostname.startsWith('192.168.') && !frontendHostname.startsWith('10.')));
    
    // Check if we're on local network (192.168.x.x or 10.x.x.x)
    isLocalNetwork = frontendHostname.startsWith('192.168.') || frontendHostname.startsWith('10.');
    
    console.log('🔍 Hostname:', frontendHostname, '| Staging:', isStagingApp, '| Is Production:', isProduction, '| Is Local Network:', isLocalNetwork);
  }
  
  // PRIORITY 1: If on local network, always use the same IP (override env vars)
  if (isLocalNetwork) {
    const networkBackendUrl = `http://${frontendHostname}:3001`;
    console.log('📱 Local network detected, overriding env vars. Using:', networkBackendUrl);
    return networkBackendUrl;
  }

  // PRIORITY 1b: Staging CRA hosts → api-staging (even if env accidentally points at prod)
  if (isStagingApp) {
    const stagingApi = 'https://api-staging.zenrentflow.com';
    if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.includes('api-staging')) {
      console.log('✅ Staging.app using REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
      return process.env.REACT_APP_API_URL;
    }
    console.log('✅ Staging.app → api-staging:', stagingApi);
    return stagingApi;
  }
  
  // PRIORITY 2: Check environment variable, but ignore if it's localhost in production
  if (process.env.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL;
    // If we're in production and env var is localhost, ignore it
    if (isProduction && (envUrl.includes('localhost') || envUrl.includes('127.0.0.1'))) {
      console.log('⚠️ Ignoring localhost env var in production');
    } else {
      console.log('✅ Using REACT_APP_API_URL env var:', envUrl);
      return envUrl;
    }
  }
  if (process.env.REACT_APP_API_BASE_URL) {
    const envUrl = process.env.REACT_APP_API_BASE_URL;
    // If we're in production and env var is localhost, ignore it
    if (isProduction && (envUrl.includes('localhost') || envUrl.includes('127.0.0.1'))) {
      console.log('⚠️ Ignoring localhost env var in production');
    } else {
      console.log('✅ Using REACT_APP_API_BASE_URL env var:', envUrl);
      return envUrl;
    }
  }
  
  // PRIORITY 3: Use production URL if we detected production
  if (isProduction) {
    console.log('✅ Using production API URL');
    return 'https://property-management-backend-production-cf85.up.railway.app';
  }
  
  // PRIORITY 4: Default to localhost for development
  console.log('🔧 Using development API URL (localhost)');
  return 'http://localhost:3001';
};

// Call at module load - but also check at runtime
const BASE_URL = getBaseURL();
console.log('🌐 API Base URL (initial):', BASE_URL);

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to get the correct base URL at runtime
const getRuntimeBaseURL = () => {
  // Check if we're in production
  let isProduction = false;
  let isStagingApp = false;
  let isLocalNetwork = false;
  let frontendHostname = 'localhost';
  
  if (typeof window !== 'undefined' && window.location) {
    frontendHostname = window.location.hostname;
    isStagingApp =
      frontendHostname === 'staging.app.zenrentflow.com' ||
      frontendHostname === 'staging-app-zenrentflow-com.web.app' ||
      frontendHostname === 'staging-app-zenrentflow-com.firebaseapp.com';
    isProduction = !isStagingApp && (
                   frontendHostname === 'app.zenrentflow.com' || 
                   frontendHostname === 'app-zenrentflow-com.web.app' ||
                   frontendHostname === 'app-zenrentflow-com.firebaseapp.com' ||
                   frontendHostname.includes('zenrentflow') ||
                   (frontendHostname !== 'localhost' && frontendHostname !== '127.0.0.1' && !frontendHostname.startsWith('192.168.') && !frontendHostname.startsWith('10.')));
    
    isLocalNetwork = frontendHostname.startsWith('192.168.') || frontendHostname.startsWith('10.');
  }
  
  // PRIORITY 1: If on local network, always use the same IP (override env vars)
  if (isLocalNetwork) {
    const networkBackendUrl = `http://${frontendHostname}:3001`;
    console.log('📱 Local network detected, using:', networkBackendUrl);
    return networkBackendUrl;
  }

  if (isStagingApp) {
    if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.includes('api-staging')) {
      return process.env.REACT_APP_API_URL;
    }
    return 'https://api-staging.zenrentflow.com';
  }
  
  // PRIORITY 2: Check environment variable, but ignore if it's localhost in production
  if (process.env.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL;
    if (!isProduction || (!envUrl.includes('localhost') && !envUrl.includes('127.0.0.1'))) {
      return envUrl;
    }
  }
  if (process.env.REACT_APP_API_BASE_URL) {
    const envUrl = process.env.REACT_APP_API_BASE_URL;
    if (!isProduction || (!envUrl.includes('localhost') && !envUrl.includes('127.0.0.1'))) {
      return envUrl;
    }
  }
  
  // PRIORITY 3: Use production URL if we detected production
  if (isProduction) {
    return 'https://property-management-backend-production-cf85.up.railway.app';
  }
  
  // PRIORITY 4: Default to localhost for development on same machine
  return 'http://localhost:3001';
};

// Add request interceptor to attach auth token and ensure correct base URL
api.interceptors.request.use(
  (config) => {
    // Update baseURL dynamically at request time
    const runtimeBaseURL = getRuntimeBaseURL();
    if (config.baseURL !== runtimeBaseURL) {
      console.log('🔄 Updating baseURL from', config.baseURL, 'to', runtimeBaseURL);
      config.baseURL = runtimeBaseURL;
    }
    
    // Get token from storage
    const token = getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Landlord screens also call /api/tenant-portal payments, so only the portal page sends the tenant session
    if (isTenantSessionUrl(config.url) && isTenantPortalPage()) {
      const tenantSession = getTenantSession();
      if (tenantSession) {
        config.headers[TENANT_SESSION_HEADER] = tenantSession.token;
      }
    }
    // Let the browser/axios set multipart boundary for FormData uploads
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      // The 10s default cancels uploads on slow mobile connections
      if (config.timeout === api.defaults.timeout) {
        config.timeout = PHOTO_UPLOAD_TIMEOUT_MS;
      }
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    console.log(`Making API request to: ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (isTenantSessionUrl(url) && isTenantPortalPage()) {
        clearTenantSession({ notify: true });
      }
      const isLoginEndpoint = url.includes('/auth/login');
      const isTenantPortalAuth = url.includes('/tenant-invitations/login') || url.includes('/tenant-invitations/activate') || url.includes('/tenant-invitations/verify-token') || url.includes('/tenant-invitations/forgot-password') || url.includes('/tenant-invitations/reset-password') || url.includes('/tenant-invitations/verify-reset-token') || url.includes('/tenant-invitations/change-password');
      const isTenantPortalRoute = typeof window !== 'undefined' && (window.location.pathname === '/tenant-portal' || window.location.pathname.startsWith('/tenant-portal/'));
      const isPortalTokenRoute = typeof window !== 'undefined' && (window.location.pathname === '/p/t' || window.location.pathname.startsWith('/p/t/'));
      const isPublicSignRoute =
        typeof window !== 'undefined' &&
        (window.location.pathname.startsWith('/sign/') ||
          window.location.pathname === '/signature-success');
      const isPublicBookingRoute =
        typeof window !== 'undefined' &&
        (window.location.pathname === '/book-showing' ||
          window.location.pathname.startsWith('/book-showing'));

      // Don't redirect for login attempts (let the form show the error)
      // Don't redirect when on tenant portal, public /p/t, e-sign, or public booking
      if (
        !isLoginEndpoint &&
        !isTenantPortalAuth &&
        !isTenantPortalRoute &&
        !isPortalTokenRoute &&
        !isPublicSignRoute &&
        !isPublicBookingRoute
      ) {
        clearSession();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

export default api;
